import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { getMobileBrowserOS } from '@/redux/modules/browser';
import { useAppSelector } from '@/redux/hooks';
import classNames from 'classnames';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import styles from './Mobile.module.scss';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

type Props = {
    alternative?: boolean;
    imageLess?: boolean;
    width?: number;
};

const Mobile = (props: Props) => {
    const { alternative, imageLess, width = 0 } = props;
    const mobileBrowserOS = useAppSelector(getMobileBrowserOS);
    const isIOS = mobileBrowserOS === 'ios';
    const isMobile = useIsNarrowerThan(767);

    const displayIOS = (isIOS && isMobile) || !isMobile;
    const displayAndroid = (!isIOS && isMobile) || !isMobile;

    if (isMobile && !alternative) {
        return null;
    }

    const mobileFooterClass = classNames(styles.mobile, Boolean(isMobile) && styles.displayMobile);
    const insideClasses = classNames(Boolean(alternative) && styles.alternative, styles.inside);

    const imageStyle = Boolean(width) ? { padding: 18, width: width } : { height: 40 };

    const badges = (
        <>
            {displayIOS && (
                <a
                    className={styles.badge}
                    href="https://itunes.apple.com/us/app/live-auctions/id321243082"
                    target="blank"
                    title="Available on the App Store"
                >
                    <ImageWithFallback
                        alt="mobile"
                        className={styles.iosBadge}
                        height="40"
                        src="https://p1.liveauctioneers.com/dist/images/app_store.svg?quality=60"
                        style={imageStyle}
                        width="119.66"
                    />
                </a>
            )}
            {displayAndroid && (
                <a
                    className={styles.badge}
                    href="https://play.google.com/store/apps/details?id=com.liveauctioneers.and"
                    target="blank"
                    title="Android App on Google Play"
                >
                    <ImageWithFallback
                        alt="mobile"
                        className={styles.googleBadge}
                        height="40"
                        src="https://p1.liveauctioneers.com/dist/images/Google_Play_Store_badge_EN.svg?quality=60"
                        style={imageStyle}
                        width="135.55"
                    />
                </a>
            )}
        </>
    );

    return imageLess ? (
        badges
    ) : (
        <div className={mobileFooterClass} id="getMobileBanner">
            <div className={insideClasses}>
                {displayIOS && (
                    <ImageWithFallback
                        alt="iphone"
                        className={classNames(styles.phoneImage, styles.iosPhoneImage)}
                        height="153"
                        src="https://p1.liveauctioneers.com/dist/images/iPhoneXs_Download_Home.png"
                        width="97"
                    />
                )}
                {displayAndroid && (
                    <ImageWithFallback
                        alt="androidPhone"
                        className={classNames(styles.phoneImage, styles.androidPhoneImage)}
                        height="136"
                        src="https://p1.liveauctioneers.com/dist/images/Pixel3_Download_Home.png"
                        width="83"
                    />
                )}
                <div className={styles.alternativeGrouping}>
                    {alternative ? (
                        <div>
                            <div className={styles.title}>
                                <FormattedMessage id="treasure_hunt" />
                            </div>
                            <div className={styles.body}>
                                <FormattedMessage id="bid_on_items_added_daily" />
                            </div>
                        </div>
                    ) : (
                        <div className={styles.footerText}>
                            <FormattedMessage id="get_notifications_from_favorite_auctioneers" />
                        </div>
                    )}
                    <div className={styles.groupedBadges}>{badges}</div>
                </div>
            </div>
        </div>
    );
};

export default React.memo<Props>(Mobile);
